import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { CATEGORIES, PROJECTS } from "./data";

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    window.__lenis?.stop();
    return () => window.__lenis?.start();
  }, []);

  return (
    <motion.div
      data-testid="project-modal"
      data-lenis-prevent
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[60] bg-[#FBF9F5] overflow-y-auto"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-12 py-10">
        <div className="flex items-center justify-between mb-10">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#6E6D69]">
            Soluzione {project.id} — {project.category}
          </p>
          <button
            data-testid="project-modal-close"
            onClick={onClose}
            aria-label="Chiudi"
            className="w-11 h-11 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <X size={18} />
          </button>
        </div>
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <h3 className="font-display text-4xl sm:text-6xl font-light text-[#181817] tracking-tight">{project.title}</h3>
          <div className="mt-8 overflow-hidden rounded-sm border border-[#E5E2DC]">
            <img src={project.image} alt={project.title} className="w-full aspect-video object-cover" />
          </div>
          <div className="mt-10 grid sm:grid-cols-3 gap-6 border-y border-[#E5E2DC] py-8">
            {[["Per chi", project.client], ["Formula", project.year], ["Zona", project.location]].map(([k, v]) => (
              <div key={k}>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">{k}</p>
                <p className="mt-2 text-[#181817]">{v}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-10 pb-16">
            {[["Esigenza", project.brief], ["Soluzione", project.concept], ["Vantaggio", project.result]].map(([k, v], i) => (
              <div key={k}>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#C85A32]">0{i + 1} · {k}</p>
                <p className="mt-4 text-base text-[#181817]/80 leading-relaxed">{v}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export const Works = () => {
  const [filter, setFilter] = useState("Tutti");
  const [selected, setSelected] = useState(null);
  const visible = filter === "Tutti" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <section id="lavori" data-testid="works-section" className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Le Nostre Soluzioni</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-[#181817]">
          Macchine e servizi per il tuo ufficio
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-12 flex flex-wrap gap-3">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(c)}
            className={`px-5 py-2 rounded-full text-sm border transition-colors duration-300 ${
              filter === c
                ? "bg-[#181817] text-[#FBF9F5] border-[#181817]"
                : "border-[#C5C2BA] text-[#6E6D69] hover:text-[#181817] hover:border-[#181817]"
            }`}
          >
            {c}
          </button>
        ))}
      </Reveal>
      <motion.div layout className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12">
        <AnimatePresence mode="popLayout">
          {visible.map((p, i) => (
            <motion.article
              layout
              key={p.id}
              data-testid={`project-card-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className={`group cursor-pointer ${i % 2 === 1 ? "md:mt-20" : ""}`}
              onClick={() => (p.b2b ? window.dispatchEvent(new Event("open-b2b-modal")) : setSelected(p))}
            >
              <div className="relative overflow-hidden rounded-sm border border-[#E5E2DC]">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className={`w-full ${p.aspect} object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
                />
                <div className="absolute inset-0 bg-[#181817]/0 group-hover:bg-[#181817]/25 transition-colors duration-500 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[#FBF9F5] text-[#181817] text-sm px-5 py-2.5 rounded-full flex items-center gap-2">
                    {p.b2b ? "Richiedi preventivo" : "Scopri di più"} <ArrowUpRight size={14} />
                  </span>
                </div>
              </div>
              <div className="mt-5 flex items-baseline justify-between">
                <h3 className="font-display text-2xl font-light text-[#181817]">{p.title}</h3>
                <span className="font-mono text-xs text-[#6E6D69]">{p.year}</span>
              </div>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">
                {p.category} · {p.location} · {p.client}
              </p>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>
      <AnimatePresence>
        {selected && <ProjectModal project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
};
